import React, { useEffect, useState, useRef } from 'react'
import DataTable, { TableColumn, TableStyles } from 'react-data-table-component'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'

import {
  TeacherAdminMetricsRval,
  OpDatum,
  TeacherDatum,
} from 'common/utils/types/web-server/api-types'

import api from 'common/services/api'

import {
  TeacherInstance,
  TeacherLicenseInfo,
} from 'common/utils/types/account-types'

import useGetCurrentUser from 'common/hooks/useGetCurrentUser'

import Logo from 'assets/xtramath-premium-logo.svg'
import Search from 'assets/magnify.svg'
import CloseIcon from 'assets/close.svg'

import {
  Box,
  Button,
  Link,
  Typography,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Paper,
  Grid,
  Stack,
} from '@mui/material'

import Modal from 'common/components/Modal'
import HighlightStatBox from './HighlightStatBox'

dayjs.extend(advancedFormat)

const customStudentTableStyles: TableStyles = {
  headCells: {
    style: {
      fontWeight: 600,
    },
  },
  cells: {
    style: {
      fontSize: '12px !important',
    },
  },
  table: {
    style: {
      '& .rdt_TableCell:first-child': {
        whiteSpace: 'normal',
        wordWrap: 'break-word',
      },
    },
  },
}

const customTeacherTableStyles: TableStyles = {
  headCells: {
    style: {
      fontWeight: 600,
      whiteSpace: 'normal',
      textAlign: 'center',
    },
  },
  cells: {
    style: {
      fontSize: '12px !important',
    },
  },
  table: {
    style: {
      '& .rdt_TableCell:first-child': {
        whiteSpace: 'normal',
        wordWrap: 'break-word',
      },
    },
  },
}

const studentsTableColumns: TableColumn<OpDatum>[] = [
  {
    name: 'Operation',
    selector: (row) => row.opName,
    wrap: true,
    conditionalCellStyles: [
      {
        when: (row) => row.opName.toLowerCase().indexOf('all') > -1,
        style: {
          fontWeight: 600,
        },
      },
    ],
  },
  {
    name: 'Total Assigned',
    selector: (row) => row.total,
    center: true,
  },
  {
    name: '< 50',
    selector: (row) => row[0],
    center: true,
  },
  {
    name: '≥ 50',
    selector: (row) => row[50],
    center: true,
  },
  {
    name: '≥ 75',
    selector: (row) => row[75],
    center: true,
  },
  {
    name: '≥ 95',
    selector: (row) => row[95],
    center: true,
  },
  {
    name: '100',
    selector: (row) => row.certificate,
    center: true,
  },
  {
    id: 'order',
    name: 'order',
    selector: (row) => row.order,
    sortable: true,
    omit: true,
  },
]

const teachersTableColumns: TableColumn<TeacherDatum>[] = [
  {
    name: 'Name',
    selector: (row) => row.name,
    sortable: true,
    wrap: true,
  },
  {
    name: 'Active Students #',
    selector: (row) => row.activeStudents.toString(),
    center: true,
  },
  {
    name: 'Ops Assigned #',
    selector: (row) => row.opsAssigned.toString(),
    center: true,
  },
  {
    name: 'Practice Days #',
    selector: (row) => row.daysOfStudentPractice.toString(),
    center: true,
  },
  {
    name: 'Awards %',
    selector: (row) => `${row.studentAwardPct.toString()}%`,
    center: true,
  },
  {
    name: 'Certificates %',
    selector: (row) => `${row.studentCertPct.toString()}%`,
    center: true,
  },
]

type Option = {
  value: string
  label: string
}
interface OperationsOption extends Option {
  coalesced: boolean
  baseOp?: string
}

const AllOperationsOption: OperationsOption = {
  value: 'all-operations',
  label: 'All Operations',
  coalesced: true,
}
const AllGradesOption: Option = { value: 'all', label: 'All Grades' }

const groupAndSortOps = (a: OpDatum, b: OpDatum) => {
  if (a.baseOp !== b.baseOp) {
    return a.baseOp.localeCompare(b.baseOp)
  }
  return a.order - b.order
}

function YearlyReport({ lCode, date }: { lCode: string; date: string }) {
  const { currentTeacher } = useGetCurrentUser()

  const [teacherSearch, setTeacherSearch] = useState('')
  const [loading, setLoading] = useState<boolean>(false)
  const [operationsFilter, setOperationsFilter] =
    useState<OperationsOption>(AllOperationsOption)
  const [gradesFilter, setGradesFilter] = useState(AllGradesOption)
  const [data, setData] = useState<TeacherAdminMetricsRval>()
  const [licenseInfo, setLicenseInfo] = useState<TeacherLicenseInfo>()
  const [error, setError] = useState<boolean>(false)
  const hasLoadedRef = useRef(false)

  const reportYear = dayjs(date).format('YYYY')

  useEffect(() => {
    hasLoadedRef.current = false
    setData(null)
    setLicenseInfo(null)
    setError(false)
  }, [lCode, date])

  useEffect(() => {
    if (!currentTeacher || hasLoadedRef.current || !date || !lCode) {
      return
    }
    document.title = `XtraMath - Yearly Report - ${dayjs(date).format(
      'MMMM YYYY'
    )}`

    hasLoadedRef.current = true
    setLoading(true)

    api({
      url: '/api2/teacher/yearly-admin-metrics',
      method: 'POST',
      data: {
        lCode,
        tCode: currentTeacher.code,
        tSecret: currentTeacher.secret,
        date,
      },
    })
      .then((response) => {
        setData(response.data)
        setLoading(false)
        window.parent.scrollTo({ top: 0, behavior: 'smooth' })
      })
      .catch(() => {
        setError(true)
        setLoading(false)
      })

    api({
      url: '/api2/license/get-info',
      method: 'POST',
      data: {
        lCode,
        tCode: currentTeacher.code,
        tSecret: currentTeacher.secret,
      },
    })
      .then((resp) => setLicenseInfo(resp.data.licenseInfo))
      .catch(() => {
        setError(true)
      })
  }, [lCode, date, currentTeacher])

  // operations filter
  const parsedCoalescedOpData: OperationsOption[] = (
    data?.coalescedOpData.all || []
  )
    .sort((a, b) => a.order - b.order)
    .map((o) => ({
      value: o.baseOp,
      label: o.opName,
      coalesced: true,
      baseOp: o.baseOp,
    }))

  const parsedOpData: OperationsOption[] = (data?.opData.all || [])
    .sort(groupAndSortOps)
    .map((o) => ({
      value: (o.opId || 0).toString(),
      label: o.opName,
      coalesced: false,
      baseOp: o.baseOp,
    }))

  const operationsFilterOptions: OperationsOption[] = [
    ...parsedCoalescedOpData,
    ...parsedOpData,
  ]

  const studentsTableFilterFn = (op: OpDatum) =>
    // eslint-disable-next-line no-nested-ternary
    operationsFilter.value === AllOperationsOption.value
      ? true
      : operationsFilter.coalesced
      ? op.baseOp === operationsFilter.baseOp
      : op.opId?.toString() === operationsFilter.value

  const studentsTableData = (
    (operationsFilter.coalesced
      ? [
          ...(data?.coalescedOpData[gradesFilter.value] || []),
          ...(data?.opData[gradesFilter.value] || []),
        ]
      : data?.opData[gradesFilter.value]) || []
  ).filter(studentsTableFilterFn)

  const parsedGrades = (
    data ? Object.keys(data.opData).filter((o) => o !== 'all') : []
  )
    .sort()
    .map((o) => ({ value: o, label: `Grade ${o}` }))

  const gradesFilterOptions: Option[] = [AllGradesOption, ...parsedGrades]

  const teachersTableData = (data?.teacherData || []).filter(
    (row) =>
      !teacherSearch.length ||
      row.name.toLowerCase().indexOf(teacherSearch.toLowerCase()) >= 0
  )

  return (
    <Box>
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Modal
        open={error}
        title='Error'
        onClose={() => window.parent.location.reload()}
        sx={{ bottom: 'unset !important' }}
      >
        <Box sx={{ p: 3 }}>
          <Typography>There was a problem loading the report.</Typography>
          <Typography sx={{ mt: 2 }}>
            Please try again or{' '}
            <Link href='mailto:xmsupport@xtramath.org'>contact us</Link> if the
            problem persists.
          </Typography>
          <Box sx={{ mt: 3, textAlign: 'right' }}>
            <Button
              variant='contained'
              color='secondary'
              onClick={() => window.parent.location.reload()}
            >
              Try Again
            </Button>
          </Box>
        </Box>
      </Modal>
      <Box sx={{ px: '3px' }}>
        <Typography variant='h4' align='center' sx={{ mb: 3 }}>
          School Year {Number(reportYear) - 1}-{dayjs(date).format('YY')} Usage
          Report
        </Typography>
        <Typography variant='h5' align='center' sx={{ mb: 3 }}>
          {dayjs(data?.range?.start).format('MMMM Do, YYYY')} -{' '}
          {dayjs(data?.range?.end).format('MMMM Do, YYYY')}
        </Typography>
        <Typography variant='h6' align='center'>
          {licenseInfo?.name || ''}
        </Typography>
        <Box sx={{ mt: 4, display: 'flex', alignItems: 'center' }}>
          <Typography variant='h5' sx={{ flex: 1 }}>
            Highlights
          </Typography>
        </Box>
        <Typography variant='body1' sx={{ mt: 3 }}>
          Discover valuable insights about all student progress and teacher
          activity during this entire school year. Learn more about{' '}
          <Link
            href='https://home.xtramath.org/support/schoolusagereports'
            target='_blank'
            rel='noopener noreferrer'
          >
            how to interpret
          </Link>{' '}
          this report. Contact{' '}
          <Link href='mailto:xmsupport@xtramath.org'>Premium support</Link> if
          you have any&nbsp;questions.
        </Typography>
        {data && (
          <Grid container spacing={2} sx={{ mt: 4 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <HighlightStatBox
                iconBg='$lightOrange'
                stat='Active Students'
                number={data.activeStudents.total.yearly.toLocaleString(
                  'en-US'
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <HighlightStatBox
                iconBg='$lightOrange'
                stat='Active Teachers'
                number={data.activeTeachers.total.yearly.toLocaleString(
                  'en-US'
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <HighlightStatBox
                iconBg='$lightOrange'
                stat='Operations Assigned'
                number={data.opsAssigned.total.yearly.toLocaleString('en-US')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <HighlightStatBox
                iconBg='$lightOrange'
                stat='Student Practice Days'
                number={data.daysPracticed.total.yearly.toLocaleString('en-US')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <HighlightStatBox
                iconBg='$lightOrange'
                stat='Certificated Students'
                number={data.certificatedStudents.total.yearly.toLocaleString(
                  'en-US'
                )}
              />
            </Grid>
          </Grid>
        )}
        <Typography variant='h5' sx={{ mt: 4 }}>
          Student Progress
        </Typography>
        <Typography variant='body1' sx={{ mt: 3 }}>
          Analyze student progress by the operations they are assigned. Each
          column indicates how many students have reached that minimum Fluency
          Score, from &lt;50&nbsp;to&nbsp;100.
        </Typography>
        <Paper
          sx={{
            mt: 3,
            boxShadow: '0px 6px 6px 0px rgba(0, 0, 0, 0.25)',
            borderRadius: 2,
          }}
        >
          <Box sx={{ px: 3, pt: 3, display: 'flex', alignItems: 'center' }}>
            {data && (
              <Stack direction='row' spacing={2} alignItems='center'>
                <Typography variant='body2'>Filter by</Typography>
                <Select
                  value={operationsFilter.value}
                  color='secondary'
                  onChange={(e) => {
                    const selected = operationsFilterOptions.find(
                      (opt) => opt.value === e.target.value
                    )
                    if (selected) setOperationsFilter(selected)
                  }}
                  displayEmpty
                  sx={{ minWidth: 200 }}
                  MenuProps={{
                    disableScrollLock: true,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    autoFocus: false,
                    disableAutoFocusItem: true,
                  }}
                >
                  <MenuItem value='' disabled>
                    Select an option
                  </MenuItem>
                  {operationsFilterOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  value={gradesFilter.value}
                  color='secondary'
                  onChange={(e) => {
                    const selected = gradesFilterOptions.find(
                      (opt) => opt.value === e.target.value
                    )
                    if (selected) setGradesFilter(selected)
                  }}
                  displayEmpty
                  sx={{ minWidth: 150 }}
                  MenuProps={{
                    disableScrollLock: true,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    autoFocus: false,
                    disableAutoFocusItem: true,
                  }}
                >
                  <MenuItem value='' disabled>
                    Select an option
                  </MenuItem>
                  {gradesFilterOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            )}
          </Box>
          {studentsTableData && (
            <DataTable
              columns={studentsTableColumns}
              data={studentsTableData}
              customStyles={customStudentTableStyles}
              striped
              defaultSortFieldId='order'
              pagination
            />
          )}
        </Paper>
        <Typography variant='h5' sx={{ mt: 4 }}>
          Teacher Activity
        </Typography>
        <Typography variant='body1' sx={{ mt: 3 }}>
          Get a comprehensive view of activity by teacher. Enter a
          Teacher&apos;s name in the search box to quickly find
          their&nbsp;information.
        </Typography>
        <Paper
          sx={{
            mt: 3,
            boxShadow: '0px 6px 6px 0px rgba(0, 0, 0, 0.25)',
            borderRadius: 2,
            pt: 3,
          }}
        >
          <Box sx={{ px: 3, pb: 2 }}>
            <TextField
              color='secondary'
              value={teacherSearch}
              placeholder='Teacher name'
              onChange={(e) => setTeacherSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Box
                      component='img'
                      src={Search}
                      alt='Search'
                      sx={{ width: 20, height: 20 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: teacherSearch ? (
                  <InputAdornment position='end'>
                    <IconButton
                      size='small'
                      onClick={() => setTeacherSearch('')}
                    >
                      <Box
                        component='img'
                        src={CloseIcon}
                        alt='Clear'
                        sx={{ width: 16, height: 16 }}
                      />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              label='Search Teacher'
              variant='outlined'
            />
          </Box>
          <DataTable
            columns={teachersTableColumns}
            data={teachersTableData}
            customStyles={customTeacherTableStyles}
            striped
            defaultSortFieldId={1}
            pagination
          />
        </Paper>
      </Box>
    </Box>
  )
}

export default YearlyReport
